import React from "react";
import { FixedSizeList } from "react-window";
import { SpreadsheetTableColumn, SpreadsheetTableColumnId, SpreadsheetTableColumnName, SpreadsheetTableDefaultDataItem, SpreadsheetTableLoadMetadata, SpreadsheetTableSelectionState } from "../../table.types";
import { RowSelectionState } from "../../utils/selection.utils";

export interface BodyProps {
  className?: string;
  spreadsheetId?: number;
  data: Array<SpreadsheetTableDefaultDataItem>;
  columns: SpreadsheetTableColumn[];
  columnsWidth: Record<SpreadsheetTableColumnId, number>;
  selectionState: SpreadsheetTableSelectionState;
  rowSelectionState?: RowSelectionState;
  /** Ref to the virtual list for scrolling to a row (e.g. after Tab navigation). */
  listRef?: React.RefObject<FixedSizeList | null>;
  onCellMouseDown: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    dataItem: SpreadsheetTableDefaultDataItem,
    columnName: SpreadsheetTableColumnName,
    event?: React.MouseEvent
  ) => void;
  onCellMouseEnter: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId
  ) => void;
  onCellMouseUp: () => void;
  isCellSelected: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId
  ) => boolean;
  onLoadData: (metadata: SpreadsheetTableLoadMetadata) => void;
  pageSize: number;
  loading?: boolean;
  onCellValueChange?: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    value: string | number,
    popPreviousUndo?: boolean
  ) => void;
  setCellToUpdate: React.Dispatch<
    React.SetStateAction<{
      rowIndex: number;
      columnId: number;
      newValue: string | number;
    } | null>
  >;
  onActivateSpreadsheet?: () => void;
}

export const useController = (props: BodyProps) => {
  // Calculate smart initial height based on available space
  const getInitialHeight = () => {
    const windowHeight = window.innerHeight;

    // Check for the main table container
    const tableContainer = document.querySelector('.SpreadsheetDetailsSpreadsheetResultTable') ||
        document.querySelector('.SpreadsheetDetailsSpreadsheetResult__table');

    if (tableContainer) {
      const containerHeight = tableContainer.clientHeight;
      if (containerHeight > 50) {
        // Use container height minus header (20px) and some padding
        const bodyHeight = containerHeight - 40;
        return Math.max(300, bodyHeight);
      }
    }

    // Fallback to window height calculation
    const fallbackHeight = Math.max(300, windowHeight - 200);
    return fallbackHeight;
  };

  const [bodyHeight, setBodyHeight] = React.useState<number>(getInitialHeight());
  const rootRef = React.useRef<HTMLDivElement>(null);

  // Use ResizeObserver to detect actual size changes
  React.useEffect(() => {
    const element = rootRef.current;
    if (!element) return;


    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        if (height > 0) {
          setBodyHeight(height);
        }
      }
    });

    resizeObserver.observe(element);

    // Also observe the parent container for changes
    const parentContainer = element.closest('.SpreadsheetDetailsSpreadsheetResultTable') ||
        element.closest('.SpreadsheetDetailsSpreadsheetResult__table');

    if (parentContainer) {
      resizeObserver.observe(parentContainer);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Fallback: check heights after initial render
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (rootRef.current) {
        const element = rootRef.current;
        const currentHeight = element.clientHeight;

        // Try parent-based calculation if current height is still small
        if (currentHeight < 100) {
          const parentHeight = element.parentElement?.clientHeight || 0;
          if (parentHeight > 50) {
            const newHeight = parentHeight - 20; // Subtract header height
            setBodyHeight(Math.max(300, newHeight));
          }
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const getItemKey = React.useCallback((index: number, data: Array<SpreadsheetTableDefaultDataItem>) => {
    return data[index]?.id!;
  }, []);


  return {
    getItemKey,
    rootRef,
    bodyHeight,
  };
};